import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";

interface Props {
  sessionId: number;
  topic: string | null;
  summary: string | null;
  durationSeconds: number;
  correctionCount: number;
  onNewSession: () => void;
}

function formatDuration(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${String(sec).padStart(2, "0")}s`;
}

export default function SessionOverlay({ sessionId, topic, summary, durationSeconds, correctionCount, onNewSession }: Props) {
  const navigate = useNavigate();

  return createPortal(
    <div className="fixed inset-0 bg-[rgba(36,28,18,0.45)] backdrop-blur-[2px] z-[100] flex items-center justify-center animate-fade-in">
      <div className="card max-w-md w-[90%] px-8 sm:px-10 py-10 text-center animate-scale-in">
        <div className="italic text-accent text-base mb-2">Alla prossima!</div>
        <h2 className="mb-4">Session complete</h2>
        {topic && (
          <p className="text-text-secondary text-[0.9375rem] mb-2 leading-relaxed">
            <span className="text-text-tertiary">Topic ·</span> {topic}
          </p>
        )}
        {summary && <p className="text-text-secondary text-[0.9375rem] mb-6 leading-relaxed">{summary}</p>}
        <div className="flex gap-3 justify-center mb-6">
          <div className="flex flex-col items-center gap-0.5 bg-surface2/60 rounded-[10px] px-5 py-3">
            <span className="font-mono text-[1.25rem] font-semibold tabular-nums text-ink">{formatDuration(durationSeconds)}</span>
            <span className="text-[0.575rem] uppercase tracking-[0.14em] text-text-tertiary font-medium">Duration</span>
          </div>
          <div className="flex flex-col items-center gap-0.5 bg-surface2/60 rounded-[10px] px-5 py-3">
            <span className="font-mono text-[1.25rem] font-semibold tabular-nums text-ink">{correctionCount}</span>
            <span className="text-[0.575rem] uppercase tracking-[0.14em] text-text-tertiary font-medium">{correctionCount === 1 ? "Correction" : "Corrections"}</span>
          </div>
        </div>
        <div className="flex gap-3 justify-center mt-2">
          <button className="btn-quiet" onClick={onNewSession}>
            New session
          </button>
          <button className="btn-primary" onClick={() => navigate(`/dashboard?session=${sessionId}`)}>
            View report
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
